import axiosInstance from '../../../shared/api/axiosInstance';
import externalAxiosInstance from '../../../external/services/externalAxiosInstance';
import type { SupportDashboard } from '../../../generated/api/models/supportDashboard';
import type {
  AgentConfig,
  AgentConfigUpdate,
  AvailableUser,
  CsatReport,
  KBArticle,
  KBArticleCreate,
  KBArticleUpdate,
  KBCategory,
  LinkedWorkItem,
  ListArticlesParams,
  ListTicketsParams,
  PortalProductsResponse,
  PortalTicketCreate,
  RoutingRule,
  RoutingRuleCreate,
  RoutingRuleUpdate,
  SLAPolicy,
  SLAPolicyCreate,
  SLAPolicyUpdate,
  Ticket,
  TicketAttachment,
  TicketCreate,
  TicketMessage,
  TicketMessageCreate,
  TicketStats,
  TicketUpdate,
  WidgetConfig,
  WidgetConfigUpdate,
  WidgetEmbedCode,
} from '../types';

const BASE_URL = '/api/v1/support';

// Ticket API
export const ticketApi = {
  list: async (params?: ListTicketsParams): Promise<{ data: Ticket[]; total: number }> => {
    // Convert arrays to comma-separated strings for the backend
    const apiParams = params ? {
      ...params,
      status: params.status?.length ? params.status.join(',') : undefined,
      priority: params.priority?.length ? params.priority.join(',') : undefined,
    } : undefined;
    const response = await axiosInstance.get(`${BASE_URL}/tickets`, { params: apiParams });
    return response.data;
  },

  get: async (id: string): Promise<Ticket> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/${id}`);
    return response.data;
  },

  getByNumber: async (ticketNumber: number): Promise<Ticket> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/by-number/${ticketNumber}`);
    return response.data;
  },

  create: async (data: TicketCreate): Promise<Ticket> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets`, data);
    return response.data;
  },

  // Portal users create tickets - contact_id derived from auth
  createPortal: async (data: PortalTicketCreate): Promise<Ticket> => {
    const response = await axiosInstance.post('/api/v1/external/support/tickets', data);
    return response.data;
  },

  update: async (id: string, data: TicketUpdate): Promise<Ticket> => {
    const response = await axiosInstance.put(`${BASE_URL}/tickets/${id}`, data);
    return response.data;
  },

  assign: async (id: string, agentId?: string, groupId?: string): Promise<Ticket> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/${id}/assign`, {
      agent_id: agentId,
      group_id: groupId,
    });
    return response.data;
  },

  getMessages: async (id: string, includeInternal = true): Promise<TicketMessage[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/${id}/messages`, {
      params: { include_internal: includeInternal },
    });
    return response.data;
  },

  addMessage: async (id: string, data: TicketMessageCreate): Promise<TicketMessage> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/${id}/messages`, data);
    return response.data;
  },

  markRead: async (id: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/tickets/${id}/mark-read`);
  },

  submitCsat: async (id: string, score: number, comment?: string): Promise<Ticket> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/${id}/csat`, {
      score,
      comment,
    });
    return response.data;
  },

  getStats: async (agentId?: string, groupId?: string): Promise<TicketStats> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/stats`, {
      params: { agent_id: agentId, group_id: groupId },
    });
    return response.data;
  },

  // Attachments
  listAttachments: async (ticketId: string): Promise<TicketAttachment[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/${ticketId}/attachments`);
    return response.data.data;
  },

  uploadAttachment: async (
    ticketId: string,
    file: File,
    messageId?: string
  ): Promise<TicketAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    if (messageId) {
      formData.append('message_id', messageId);
    }
    const response = await axiosInstance.post(
      `${BASE_URL}/tickets/${ticketId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  getAttachment: async (ticketId: string, attachmentId: string): Promise<TicketAttachment> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}`
    );
    return response.data.data;
  },

  downloadAttachment: async (ticketId: string, attachmentId: string): Promise<Blob> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  deleteAttachment: async (ticketId: string, attachmentId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}`);
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/tickets/${id}`);
  },

  // Work item integration
  createWorkItem: async (ticketId: string): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/${ticketId}/work-item`);
    return response.data;
  },

  getLinkedWorkItem: async (ticketId: string): Promise<{ linked_work_item: LinkedWorkItem | null }> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/${ticketId}/work-item`);
    return response.data;
  },

  // Bulk operations
  bulkUpdate: async (ids: string[], data: TicketUpdate): Promise<{ updated: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/bulk-update`, {
      ids,
      ...data,
    });
    return response.data;
  },

  bulkDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/bulk-delete`, { ids });
    return response.data;
  },

  // Get files linked to ticket via backlinks (from file management module)
  getLinkedFiles: async (ticketId: string): Promise<TicketAttachment['file'][]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/${ticketId}/files`);
    return response.data.data;
  },
};

// Dashboard API
export const dashboardApi = {
  get: async (): Promise<SupportDashboard> => {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard`);
    return response.data;
  },
  getCsatReport: async (startDate: string, endDate: string): Promise<CsatReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/csat-reports`, {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },
};

// Knowledge Base API
export const kbApi = {
  // Categories
  listCategories: async (parentId?: string, publicOnly = false): Promise<KBCategory[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/categories`, {
      params: { parent_id: parentId, public_only: publicOnly },
    });
    return response.data;
  },

  getCategory: async (id: string): Promise<KBCategory> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/categories/${id}`);
    return response.data;
  },

  createCategory: async (data: {
    name: string;
    slug: string;
    description?: string;
    parent_id?: string;
    position?: number;
    icon?: string;
    product_ids?: string[];
    is_public?: boolean;
  }): Promise<KBCategory> => {
    const response = await axiosInstance.post(`${BASE_URL}/kb/categories`, data);
    return response.data;
  },

  updateCategory: async (
    id: string,
    data: Partial<{
      name: string;
      slug: string;
      description: string;
      position: number;
      icon: string;
      product_ids: string[];
      is_public: boolean;
    }>
  ): Promise<KBCategory> => {
    const response = await axiosInstance.patch(`${BASE_URL}/kb/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/kb/categories/${id}`);
  },

  // Articles
  listArticles: async (params?: ListArticlesParams): Promise<{ data: KBArticle[]; total: number }> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/articles`, { params });
    return response.data;
  },

  getArticle: async (id: string): Promise<KBArticle> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/articles/${id}`);
    return response.data;
  },

  getArticleBySlug: async (slug: string): Promise<KBArticle> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/articles/by-slug/${slug}`);
    return response.data;
  },

  createArticle: async (data: KBArticleCreate): Promise<KBArticle> => {
    const response = await axiosInstance.post(`${BASE_URL}/kb/articles`, data);
    return response.data;
  },

  updateArticle: async (id: string, data: KBArticleUpdate): Promise<KBArticle> => {
    const response = await axiosInstance.patch(`${BASE_URL}/kb/articles/${id}`, data);
    return response.data;
  },

  publishArticle: async (id: string): Promise<KBArticle> => {
    const response = await axiosInstance.post(`${BASE_URL}/kb/articles/${id}/publish`);
    return response.data;
  },

  archiveArticle: async (id: string): Promise<KBArticle> => {
    const response = await axiosInstance.post(`${BASE_URL}/kb/articles/${id}/archive`);
    return response.data;
  },

  deleteArticle: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/kb/articles/${id}`);
  },

  recordView: async (id: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/kb/articles/${id}/view`);
  },

  recordFeedback: async (id: string, helpful: boolean): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/kb/articles/${id}/feedback`, { helpful });
  },
};

// Portal API (for external users)
const PORTAL_BASE_URL = '/api/v1/external/support';

export const portalApi = {
  listTickets: async (params?: ListTicketsParams): Promise<{ data: Ticket[]; total: number }> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/tickets`, { params });
    return response.data;
  },

  getTicket: async (id: string): Promise<Ticket> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/tickets/${id}`);
    return response.data;
  },

  createTicket: async (data: PortalTicketCreate): Promise<Ticket> => {
    const response = await externalAxiosInstance.post(`${PORTAL_BASE_URL}/tickets`, data);
    return response.data;
  },

  getMessages: async (id: string): Promise<TicketMessage[]> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/tickets/${id}/messages`);
    return response.data;
  },

  addMessage: async (id: string, bodyText: string, bodyHtml?: string): Promise<TicketMessage> => {
    const response = await externalAxiosInstance.post(`${PORTAL_BASE_URL}/tickets/${id}/messages`, {
      body_text: bodyText,
      body_html: bodyHtml,
    });
    return response.data;
  },

  submitCsat: async (id: string, score: number, comment?: string): Promise<Ticket> => {
    const response = await externalAxiosInstance.post(`${PORTAL_BASE_URL}/tickets/${id}/csat`, {
      score,
      comment,
    });
    return response.data;
  },

  resolveTicket: async (id: string): Promise<Ticket> => {
    const response = await externalAxiosInstance.post(`${PORTAL_BASE_URL}/tickets/${id}/resolve`);
    return response.data;
  },

  listAttachments: async (ticketId: string): Promise<TicketAttachment[]> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/tickets/${ticketId}/attachments`);
    return response.data.data;
  },

  downloadAttachment: async (ticketId: string, attachmentId: string): Promise<Blob> => {
    const response = await externalAxiosInstance.get(
      `${PORTAL_BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  listProducts: async (): Promise<PortalProductsResponse> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/products`);
    return response.data;
  },
};

// SLA Policy API
export const slaApi = {
  list: async (activeOnly = false): Promise<SLAPolicy[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/sla-policies`, {
      params: { active_only: activeOnly },
    });
    return response.data;
  },

  get: async (id: string): Promise<SLAPolicy> => {
    const response = await axiosInstance.get(`${BASE_URL}/sla-policies/${id}`);
    return response.data;
  },

  create: async (data: SLAPolicyCreate): Promise<SLAPolicy> => {
    const response = await axiosInstance.post(`${BASE_URL}/sla-policies`, data);
    return response.data;
  },

  update: async (id: string, data: SLAPolicyUpdate): Promise<SLAPolicy> => {
    const response = await axiosInstance.put(`${BASE_URL}/sla-policies/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/sla-policies/${id}`);
  },
};

// Agent API
export const agentApi = {
  list: async (includeOffline = true): Promise<AgentConfig[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/agents`, {
      params: { include_offline: includeOffline },
    });
    return response.data;
  },

  get: async (userId: string): Promise<AgentConfig> => {
    const response = await axiosInstance.get(`${BASE_URL}/agents/${userId}`);
    return response.data;
  },

  create: async (userId: string, data: AgentConfigUpdate): Promise<AgentConfig> => {
    const response = await axiosInstance.post(`${BASE_URL}/agents/${userId}`, data);
    return response.data;
  },

  update: async (userId: string, data: AgentConfigUpdate): Promise<AgentConfig> => {
    const response = await axiosInstance.put(`${BASE_URL}/agents/${userId}`, data);
    return response.data;
  },

  toggleAvailability: async (userId: string): Promise<AgentConfig> => {
    const response = await axiosInstance.post(`${BASE_URL}/agents/${userId}/toggle-availability`);
    return response.data;
  },

  delete: async (userId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/agents/${userId}`);
  },

  getAvailableUsers: async (): Promise<AvailableUser[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/agents/available-users`);
    return response.data;
  },
};

// Routing Rules API
export const routingRulesApi = {
  list: async (activeOnly = false): Promise<RoutingRule[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/routing-rules`, {
      params: { active_only: activeOnly },
    });
    return response.data;
  },

  get: async (id: string): Promise<RoutingRule> => {
    const response = await axiosInstance.get(`${BASE_URL}/routing-rules/${id}`);
    return response.data;
  },

  create: async (data: RoutingRuleCreate): Promise<RoutingRule> => {
    const response = await axiosInstance.post(`${BASE_URL}/routing-rules`, data);
    return response.data;
  },

  update: async (id: string, data: RoutingRuleUpdate): Promise<RoutingRule> => {
    const response = await axiosInstance.put(`${BASE_URL}/routing-rules/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/routing-rules/${id}`);
  },

  reorder: async (ruleIds: string[]): Promise<RoutingRule[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/routing-rules/reorder`, {
      rule_ids: ruleIds,
    });
    return response.data;
  },
};

// Widget API
export const widgetApi = {
  getConfig: async (): Promise<WidgetConfig> => {
    const response = await axiosInstance.get(`${BASE_URL}/widget/config`);
    return response.data;
  },

  updateConfig: async (data: WidgetConfigUpdate): Promise<WidgetConfig> => {
    const response = await axiosInstance.put(`${BASE_URL}/widget/config`, data);
    return response.data;
  },

  regenerateKey: async (): Promise<{ widget_key: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/widget/regenerate-key`);
    return response.data;
  },

  getEmbedCode: async (): Promise<WidgetEmbedCode> => {
    const response = await axiosInstance.get(`${BASE_URL}/widget/embed-code`);
    return response.data;
  },
};
